import React, {useEffect, useMemo, useState} from "react";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {OverlayTrigger, Tooltip} from "react-bootstrap";
import {faExclamationCircle, faInfoCircle, faArrowUp, faArrowDown} from "@fortawesome/free-solid-svg-icons";
import {useLocation, useNavigate} from "react-router-dom";
import {defaultFormState} from "../../../../defaults/Forms";
import {departmentDefault} from "../../../../defaults/Items";
import {
  compareData,
  onCloseDetails,
  onItemsListDeleteButtonClick,
  onSaveAndClose,
  restoreFormData,
  setForegroundColor
} from "../../../../helpers/ComponentHelper";
import AppComponentCardHeader from "../../common/AppComponentCardHeader";
import LoadingDataDiv from "../../common/LoadingDataDiv";
import DeleteWarningModal from "../../common/DeleteWarningModal";
import SettingsEntityModal from "../../common/SettingsEntityModal";
import TextInput from "../../../elements/TextInput";
import TextArea from "../../../elements/TextArea";
import useCrud from "../../../../hooks/useCrud";
import {Table} from "../../../table/Table";
import RelatedItemsList from "../../../elements/RelatedItemsList";
import {HexColorPicker} from "react-colorful";
import "../../../../css/ResourceForms.css";

const CRITERIA = [
  {value: "LOWEST_COST", label: "Najniższy koszt", tooltip: "Preferuje pracowników o najniższym pełnym koszcie roboczogodziny."},
  {value: "HIGHEST_EVALUATION", label: "Najwyższa ocena", tooltip: "Preferuje pracowników z najwyższą bieżącą oceną ogólną."},
  {value: "FEWEST_WORKING_DAYS", label: "Najmniej dni pracy", tooltip: "Preferuje pracowników z najmniejszą liczbą już zaplanowanych dni pracy."},
  {value: "PREFERRED_AVAILABILITY", label: "Preferowana dostępność", tooltip: "Premiuje deklarację PREFERRED obejmującą całą zmianę."}
];

const PERIODS = [
  {type: "DAILY", field: "defaultDailyResourcePlanId", label: "Dzienny"},
  {type: "WEEKEND", field: "defaultWeekendResourcePlanId", label: "Weekendowy"},
  {type: "WEEKLY", field: "defaultWeeklyResourcePlanId", label: "Tygodniowy"},
  {type: "BIWEEKLY", field: "defaultBiweeklyResourcePlanId", label: "Dwutygodniowy"},
  {type: "MONTHLY", field: "defaultMonthlyResourcePlanId", label: "Miesięczny"}
];

const HelpTooltip = ({id, text}) => (
  <OverlayTrigger placement="top" overlay={<Tooltip id={`department-help-${id}`} className="department-field-tooltip">{text}</Tooltip>}>
    <span className="department-field-help-wrap" tabIndex={0} aria-label={text}>
      <FontAwesomeIcon icon={faInfoCircle} className="department-field-help"/>
    </span>
  </OverlayTrigger>
);

const FieldLabel = ({htmlFor, children, tooltip}) => (
  <label htmlFor={htmlFor} className="department-field-label">
    <span>{children}</span>
    {tooltip && <HelpTooltip id={htmlFor} text={tooltip}/>}
  </label>
);

const Department = () => {
  const dataUrl = "/departments";
  const settingsUrl = "/settings";
  const defaultItem = departmentDefault;
  const itemName = "dział firmy";
  const navigate = useNavigate();
  const location = useLocation();
  const {createItem, updateItem, deleteItem, getItems} = useCrud(dataUrl);

  const [loading, setLoading] = useState(true);
  const [allowDelete, setAllowDelete] = useState(null);
  const [currentFormState, setCurrentFormState] = useState(defaultFormState);
  const [itemsList, setItems] = useState([]);
  const [currentItem, setCurrentItem] = useState(defaultItem);
  const [backupItem, setBackupItem] = useState(defaultItem);
  const [itemChanged, setItemChanged] = useState(false);
  const [updateConflict, setUpdateConflict] = useState(false);
  const [conflictingRoles, setConflictingRoles] = useState(false);
  const [leadershipCandidates, setLeadershipCandidates] = useState([]);
  const [departmentEmployeesList, setDepartmentEmployeesList] = useState([]);
  const [positionsList, setPositionsList] = useState([]);
  const [resourcePlans, setResourcePlans] = useState([]);
  const [settings, setSettings] = useState({});
  const [conflictingRolesMsg, setConflictingRolesMsg] = useState("");
  const [validationError, setValidationError] = useState("");
  const [itemColor, setItemColor] = useState("#ffffff");

  const state = {
    itemsList, setItems,
    currentItem, setCurrentItem,
    currentFormState, setCurrentFormState,
    defaultItem, backupItem, setBackupItem, itemChanged, setItemChanged,
    setAllowDelete,
    setRelatedItems: null
  };

  const leadershipEmployeesList = useMemo(() => [...leadershipCandidates]
    .sort((a, b) => `${a.lastName || ""} ${a.firstName || ""}`.localeCompare(`${b.lastName || ""} ${b.firstName || ""}`, "pl")),
  [leadershipCandidates]);

  const leadershipPositions = useMemo(() => positionsList
    .filter(position => position.active !== false)
    .filter(position => position.positionLevel === "FUNCTIONAL" || position.positionLevel === "MANAGEMENT")
    .sort((a, b) => String(a.name || "").localeCompare(String(b.name || ""), "pl")),
  [positionsList]);

  const leadershipCandidate = employee => {
    const id = Number(employee?.id);
    return leadershipCandidates.find(candidate => Number(candidate.id) === id) || null;
  };

  const selectLeader = (field, positionField, value) => {
    const id = Number(value);
    const candidate = leadershipCandidates.find(item => Number(item.id) === id) || null;
    setValidationError("");
    touch(current => ({...current, [field]: candidate, [positionField]: null}));
  };


  const criteriaOrder = useMemo(() => {
    const configured = String(currentItem.scheduleGenerationCriteria || "")
      .split(",")
      .map(value => value.trim())
      .filter(value => CRITERIA.some(criterion => criterion.value === value));
    const result = [...new Set(configured)];
    CRITERIA.forEach(criterion => {
      if (!result.includes(criterion.value)) result.push(criterion.value);
    });
    return result;
  }, [currentItem.scheduleGenerationCriteria]);

  const columns = [
    {label: "Id", accessor: "id", sortable: true, searchable: false, visible: false},
    {label: "Nazwa", accessor: "name", sortable: true, searchable: true},
    {label: "Skrócona nazwa", accessor: "shortName", sortable: true, searchable: true},
    {label: "Email", accessor: "email", sortable: true, searchable: true},
    {label: "Telefon", accessor: "phoneNumber", sortable: true, searchable: true},
    {label: "Limit urlopów", accessor: "vacationLimit", sortable: true, searchable: false, format: value => value == null ? "Brak" : value},
    {label: "Po przekroczeniu", accessor: "vacationLimitAction", sortable: true, searchable: false, format: value => value === "BLOCK" ? "Blokuj" : "Ostrzegaj"},
    {label: "Kierownik", accessor: "manager", sortable: true, searchable: true, format: manager => manager ? `${manager.lastName} ${manager.firstName}` : "", fieldsToInclude: ["firstName", "lastName"]},
    {label: "Zastępca", accessor: "deputyManager", sortable: true, searchable: true, format: deputy => deputy ? `${deputy.lastName} ${deputy.firstName}` : "", fieldsToInclude: ["firstName", "lastName"]},
    {label: "Kolor", accessor: "color", sortable: false, searchable: false, type: "color"},
    {label: "Edycja", accessor: "editBtn", sortable: false, searchable: false},
    {label: "Usuń", accessor: "deleteBtn", sortable: false, searchable: false}
  ];

  const touch = updater => {
    setItemChanged(value => !value);
    setCurrentItem(current => typeof updater === "function" ? updater(current) : updater);
    setCurrentFormState(current => ({...current, formSaveButtonDisabled: false}));
  };

  const setField = (field, value) => {
    setValidationError("");
    touch(current => ({...current, [field]: value}));
  };

  const numberOrNull = value => value === "" || value == null ? null : Number(value);

  const onDelete = async () => {
    const response = await deleteItem(`${dataUrl}/${currentItem.id}`, currentItem.id, state);
    if (response === 401 || response === 403) navigate("/login", {state: {from: location}, replace: true});
  };

  const validate = () => {
    if (!currentItem.name || !currentItem.shortName) return "Nazwa i skrót są wymagane.";
    const integerFields = [
      ["vacationLimit", 1, 10000, "Limit osób na urlopie"],
      ["defaultMinimumStaff", 0, 10000, "Minimalna obsada"],
      ["defaultMaximumStaff", 0, 10000, "Maksymalna obsada"],
      ["scheduleRequiredDaysOffPerWeek", 0, 7, "Dni wolne w tygodniu"],
      ["scheduleMaxConsecutiveWorkDays", 1, 366, "Maksymalna liczba kolejnych dni pracy"],
      ["scheduleConsecutiveDaysWarningThreshold", 1, 366, "Próg ostrzeżenia"],
      ["scheduleDayOffMinimumAdvanceDays", 0, 3650, "Wyprzedzenie prośby o wolne"],
      ["scheduleVacationMinimumAdvanceDays", 0, 3650, "Wyprzedzenie urlopu"]
    ];
    for (const [field, min, max, label] of integerFields) {
      const value = numberOrNull(currentItem[field]);
      if (value != null && (!Number.isInteger(value) || value < min || value > max)) {
        return `${label}: dozwolony zakres ${min}–${max}.`;
      }
    }
    const minStaff = numberOrNull(currentItem.defaultMinimumStaff);
    const maxStaff = numberOrNull(currentItem.defaultMaximumStaff);
    if (minStaff != null && maxStaff != null && maxStaff < minStaff) return "Maksymalna obsada nie może być mniejsza od minimalnej.";
    const col = numberOrNull(currentItem.defaultTargetColPercent);
    if (col != null && (col < 0 || col > 100)) return "COL TARGET musi być z zakresu 0–100%.";
    const spmh = numberOrNull(currentItem.defaultTargetSpmh);
    if (spmh != null && spmh <= 0) return "SPMH TARGET musi być większy od zera.";
    const budget = numberOrNull(currentItem.defaultLaborHoursBudget);
    if (budget != null && budget < 0) return "Budżet roboczogodzin nie może być ujemny.";
    if (currentItem.manager) {
      const candidate = leadershipCandidate(currentItem.manager);
      if (candidate && !candidate.leadershipQualified && !numberOrNull(currentItem.managerPositionId)) {
        return "Wybierz stanowisko Funkcyjne lub Kierownicze dla kierownika.";
      }
    }
    if (currentItem.deputyManager) {
      const candidate = leadershipCandidate(currentItem.deputyManager);
      if (candidate && !candidate.leadershipQualified && !numberOrNull(currentItem.deputyManagerPositionId)) {
        return "Wybierz stanowisko Funkcyjne lub Kierownicze dla zastępcy.";
      }
    }
    return "";
  };

  const onSaveItemClick = async event => {
    event.preventDefault();
    const error = validate();
    if (error) {
      setValidationError(error);
      return;
    }

    const item = {
      id: currentItem.id,
      name: currentItem.name,
      shortName: currentItem.shortName,
      description: currentItem.description,
      manager: currentItem.manager?.id > 0 ? {id: currentItem.manager.id} : null,
      deputyManager: currentItem.deputyManager?.id > 0 ? {id: currentItem.deputyManager.id} : null,
      managerPositionId: numberOrNull(currentItem.managerPositionId),
      deputyManagerPositionId: numberOrNull(currentItem.deputyManagerPositionId),
      email: currentItem.email,
      phoneNumber: currentItem.phoneNumber,
      color: currentItem.color,
      vacationLimit: numberOrNull(currentItem.vacationLimit),
      vacationLimitAction: currentItem.vacationLimitAction || "WARN",
      scheduleGenerationCriteria: criteriaOrder.join(","),
      defaultTargetColPercent: numberOrNull(currentItem.defaultTargetColPercent),
      defaultTargetSpmh: numberOrNull(currentItem.defaultTargetSpmh),
      defaultLaborHoursBudget: numberOrNull(currentItem.defaultLaborHoursBudget),
      defaultMinimumStaff: numberOrNull(currentItem.defaultMinimumStaff),
      defaultMaximumStaff: numberOrNull(currentItem.defaultMaximumStaff),
      scheduleRequiredDaysOffPerWeek: numberOrNull(currentItem.scheduleRequiredDaysOffPerWeek),
      scheduleMaxConsecutiveWorkDays: numberOrNull(currentItem.scheduleMaxConsecutiveWorkDays),
      scheduleConsecutiveDaysWarningThreshold: numberOrNull(currentItem.scheduleConsecutiveDaysWarningThreshold),
      scheduleDayOffMinimumAdvanceDays: numberOrNull(currentItem.scheduleDayOffMinimumAdvanceDays),
      scheduleVacationMinimumAdvanceDays: numberOrNull(currentItem.scheduleVacationMinimumAdvanceDays),
      defaultDailyResourcePlanId: numberOrNull(currentItem.defaultDailyResourcePlanId),
      defaultWeekendResourcePlanId: numberOrNull(currentItem.defaultWeekendResourcePlanId),
      defaultWeeklyResourcePlanId: numberOrNull(currentItem.defaultWeeklyResourcePlanId),
      defaultBiweeklyResourcePlanId: numberOrNull(currentItem.defaultBiweeklyResourcePlanId),
      defaultMonthlyResourcePlanId: numberOrNull(currentItem.defaultMonthlyResourcePlanId)
    };

    const response = currentFormState.formAddingDataMode
      ? await createItem(dataUrl, item, state)
      : await updateItem(`${dataUrl}/${item.id}`, item, state);

    if (response.status === 401 || response.status === 403) {
      navigate("/login", {state: {from: location}, replace: true});
    } else if (response.status === 409) {
      setUpdateConflict(true);
    } else if (response.status === 422) {
      setConflictingRolesMsg(typeof response.data === "string" ? response.data : response.data?.message || "Nie można zapisać działu.");
      setConflictingRoles(true);
    } else if (response) {
      onSaveAndClose({state});
    }
  };

  const checkRelatedItems = () => setAllowDelete(true);

  const changeColor = color => {
    setItemColor(color);
    setField("color", color);
  };

  const moveCriterion = (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= criteriaOrder.length) return;
    const next = [...criteriaOrder];
    [next[index], next[target]] = [next[target], next[index]];
    setField("scheduleGenerationCriteria", next.join(","));
  };

  const onClose = () => {
    setUpdateConflict(false);
    setValidationError("");
    onCloseDetails({state});
  };

  useEffect(() => {
    if (updateConflict) setUpdateConflict(false);
    if (conflictingRoles) setConflictingRoles(false);
    compareData(currentFormState, setCurrentFormState, currentItem, backupItem);
  }, [itemChanged]);

  useEffect(() => {
    if (allowDelete !== null) onItemsListDeleteButtonClick(currentFormState, setCurrentFormState, itemName, allowDelete, currentItem.name);
  }, [allowDelete]);

  useEffect(() => {
    const getData = async () => {
      const [itemsData, settingsData] = await Promise.all([
        getItems(dataUrl),
        getItems(settingsUrl)
      ]);
      const responses = [itemsData, settingsData];
      if (responses.some(response => response.status === 401 || response.status === 403)) {
        navigate("/login", {state: {from: location}, replace: true});
        return;
      }
      if (responses.every(response => response.status === 200)) {
        setItems(itemsData.data || []);
        setSettings(settingsData.data || {});
      } else {
        alert("Nie udało się pobrać danych.");
      }
      setLoading(false);
    };
    getData();
  }, []);

  useEffect(() => {
    if (currentItem.manager && currentItem.deputyManager && currentItem.manager.id === currentItem.deputyManager.id) {
      setConflictingRoles(true);
      setConflictingRolesMsg("Ta sama osoba nie może być jednocześnie kierownikiem i zastępcą w tym samym dziale.");
    } else {
      setConflictingRoles(false);
      setConflictingRolesMsg("");
    }
  }, [currentItem.manager, currentItem.deputyManager]);

  useEffect(() => {
    const loadRelations = async () => {
      if (!currentItem.id) {
        setDepartmentEmployeesList([]);
        setPositionsList([]);
        setLeadershipCandidates([]);
        setResourcePlans([]);
        return;
      }
      const [positionsResponse, employeesResponse, leadershipResponse, plansResponse] = await Promise.all([
        getItems(`/departments/${currentItem.id}/positions`),
        getItems(`/departments/${currentItem.id}/employees`),
        getItems(`/departments/${currentItem.id}/leadership-candidates`),
        getItems(`/departments/${currentItem.id}/generation-plans`)
      ]);
      setPositionsList(positionsResponse.status === 200 ? positionsResponse.data || [] : []);
      setDepartmentEmployeesList(employeesResponse.status === 200 ? employeesResponse.data || [] : []);
      setLeadershipCandidates(leadershipResponse.status === 200 ? leadershipResponse.data || [] : []);
      setResourcePlans(plansResponse.status === 200 ? plansResponse.data || [] : []);
    };
    loadRelations();
  }, [currentItem.id]);

  const getCompleteItem = async id => {
    const response = await getItems(`${dataUrl}/${id}`);
    if (response.status === 200) return response.data;
    if (response.status === 401 || response.status === 403) navigate("/login", {state: {from: location}, replace: true});
    else alert("Nie udało się pobrać danych.");
  };

  const addDataButtonProps = {
    setCurrentItem,
    setBackupItem,
    defaultItem,
    currentFormState,
    setCurrentFormState,
    formDescription: "",
    formHeader: "Nowy dział",
    buttonTitle: "Dodaj dział"
  };

  const input = (field, props = {}) => (
    <input
      id={field}
      name={field}
      className="form-control form-control-sm"
      value={currentItem[field] ?? ""}
      onChange={event => setField(field, event.target.value)}
      {...props}
    />
  );

  const inherited = (field, fallback, suffix = "") => {
    const value = settings[field];
    return value == null ? fallback : `${value}${suffix}`;
  };

  let dataSectionContent;
  if (loading) {
    dataSectionContent = <LoadingDataDiv/>;
  } else if (itemsList.length > 0) {
    dataSectionContent = (
      <Table
        rows={itemsList}
        columns={columns}
        state={state}
        checkRelatedItems={checkRelatedItems}
        formHeader={`Edycja ${itemName}`}
        relatedItemsUrl={null}
        getCompleteItem={getCompleteItem}
        tableClassName="resource-list-table resource-crud-table"
        tableContainerClassName="resource-list-table-scroll"
      />
    );
  } else {
    dataSectionContent = (
      <div className="warn_info">
        <FontAwesomeIcon className="warning-icon div-visible" icon={faExclamationCircle}/>&nbsp;&nbsp;&nbsp;NIE ZNALEZIONO ŻADNYCH DANYCH
      </div>
    );
  }

  return (
    <div id="layoutSidenav_content" className="settings-entity-page resource-page">
      <AppComponentCardHeader title="Działy firmy" buttonProps={addDataButtonProps}/>

      <div className="container-fluid px-4 py-3">
        <div className="card shadow-sm mb-5 resource-list-card resource-standard-list-card">{dataSectionContent}</div>
      </div>

      <SettingsEntityModal
        show={currentFormState.showForm}
        onClose={onClose}
        title={currentFormState.formAddingDataMode ? "Nowy dział" : `Edycja: ${currentItem.name || "Dział"}`}
        adding={currentFormState.formAddingDataMode}
        introText="Konfiguracja organizacyjna i domyślne zasady planowania dla działu."
        onRestore={() => { setUpdateConflict(false); restoreFormData({state}); }}
        onDelete={onDelete}
        onSubmit={onSaveItemClick}
        state={state}
        size="xl"
      >
        <form id="add-department-form" name="add-department-form" className="department-form-card department-form-compact">
          <section className="settings-form-section department-compact-section">
            <div className="settings-form-section-heading department-compact-heading">
              <div><span className="settings-form-section-kicker">Dział</span><h6>Dane i kierownictwo</h6></div>
              <div className="department-color-preview" style={{"--department-color": currentItem.color || itemColor || "#7b157e"}}>
                <span className="department-color-preview-dot"></span>
                <span>{currentItem.name || "Podgląd"}</span>
              </div>
            </div>

            <div className="department-form-grid department-form-grid-compact">
              <div className="dictionary-form-field">
                <FieldLabel htmlFor="name" tooltip="Pełna nazwa działu widoczna w grafikach, raportach i kartotekach pracowników.">Nazwa *</FieldLabel>
                <TextInput propertyName="name" required={true} state={state}/>
              </div>
              <div className="dictionary-form-field">
                <FieldLabel htmlFor="shortName" tooltip="Krótka nazwa używana w tabelach i innych miejscach o ograniczonej szerokości.">Skrót *</FieldLabel>
                <TextInput propertyName="shortName" maxlen="10" required={true} state={state}/>
              </div>
              <div className="dictionary-form-field">
                <FieldLabel htmlFor="email" tooltip="Opcjonalny wspólny adres kontaktowy działu.">Email</FieldLabel>
                <TextInput propertyName="email" required={false} maxlen="120" state={state}/>
              </div>
              <div className="dictionary-form-field">
                <FieldLabel htmlFor="phoneNumber" tooltip="Opcjonalny numer kontaktowy do działu.">Telefon</FieldLabel>
                <TextInput propertyName="phoneNumber" required={false} maxlen="30" state={state}/>
              </div>
              <div className="dictionary-form-field">
                <FieldLabel htmlFor="manager" tooltip="Możesz wskazać dowolnego aktywnego pracownika. Jeśli nie ma jeszcze stanowiska Funkcyjnego lub Kierowniczego w tym dziale, wybierz je w polu obok; system przypisze je przy zapisie. Funkcja kierownika automatycznie nada standardowe uprawnienia kierownicze powiązanemu kontu.">Kierownik</FieldLabel>
                <select id="manager" className="form-select form-select-sm" disabled={!currentItem.id} value={currentItem.manager?.id || ""} onChange={event => selectLeader("manager", "managerPositionId", event.target.value)}>
                  <option value="">{currentItem.id ? "Brak" : "Dostępne po zapisaniu działu"}</option>
                  {leadershipEmployeesList.map(employee => <option key={employee.id} value={employee.id}>{employee.firstName} {employee.lastName}{employee.leadershipQualified ? "" : employee.assignedToDepartment ? " — wybierz stanowisko funkcyjne/kierownicze" : " — przypisz do działu"}</option>)}
                </select>
              </div>
              {currentItem.manager && leadershipCandidate(currentItem.manager) && !leadershipCandidate(currentItem.manager).leadershipQualified && (
                <div className="dictionary-form-field">
                  <FieldLabel htmlFor="managerPositionId" tooltip="Stanowisko zostanie aktywnie przypisane pracownikowi w tym dziale podczas zapisu. Samo stanowisko nie nadaje uprawnień; uprawnienia kierownicze wynikają z funkcji Kierownika działu.">Stanowisko kierownika *</FieldLabel>
                  <select id="managerPositionId" className="form-select form-select-sm" value={currentItem.managerPositionId || ""} onChange={event => setField("managerPositionId", event.target.value)}>
                    <option value="">Wybierz stanowisko</option>
                    {leadershipPositions.map(position => <option key={position.id} value={position.id}>{position.name} ({position.positionLevel === "MANAGEMENT" ? "Kierownicze" : "Funkcyjne"})</option>)}
                  </select>
                </div>
              )}
              <div className="dictionary-form-field">
                <FieldLabel htmlFor="deputyManager" tooltip="Możesz wskazać dowolnego aktywnego pracownika. Jeśli nie ma jeszcze stanowiska Funkcyjnego lub Kierowniczego w tym dziale, wybierz je w polu obok; system przypisze je przy zapisie. Funkcja zastępcy daje taki sam standardowy zakres kierowniczy dla tego działu.">Zastępca</FieldLabel>
                <select id="deputyManager" className="form-select form-select-sm" disabled={!currentItem.id} value={currentItem.deputyManager?.id || ""} onChange={event => selectLeader("deputyManager", "deputyManagerPositionId", event.target.value)}>
                  <option value="">{currentItem.id ? "Brak" : "Dostępne po zapisaniu działu"}</option>
                  {leadershipEmployeesList.map(employee => <option key={employee.id} value={employee.id}>{employee.firstName} {employee.lastName}{employee.leadershipQualified ? "" : employee.assignedToDepartment ? " — wybierz stanowisko funkcyjne/kierownicze" : " — przypisz do działu"}</option>)}
                </select>
              </div>
              {currentItem.deputyManager && leadershipCandidate(currentItem.deputyManager) && !leadershipCandidate(currentItem.deputyManager).leadershipQualified && (
                <div className="dictionary-form-field">
                  <FieldLabel htmlFor="deputyManagerPositionId" tooltip="Stanowisko zostanie aktywnie przypisane pracownikowi w tym dziale podczas zapisu. Funkcja Zastępcy działu jest źródłem standardowych uprawnień kierowniczych.">Stanowisko zastępcy *</FieldLabel>
                  <select id="deputyManagerPositionId" className="form-select form-select-sm" value={currentItem.deputyManagerPositionId || ""} onChange={event => setField("deputyManagerPositionId", event.target.value)}>
                    <option value="">Wybierz stanowisko</option>
                    {leadershipPositions.map(position => <option key={position.id} value={position.id}>{position.name} ({position.positionLevel === "MANAGEMENT" ? "Kierownicze" : "Funkcyjne"})</option>)}
                  </select>
                </div>
              )}
              <div className="dictionary-form-field dictionary-form-field-full">
                <FieldLabel htmlFor="description" tooltip="Opcjonalny krótki opis zakresu odpowiedzialności działu.">Opis</FieldLabel>
                <TextArea propertyName="description" required={false} rows="2" state={state}/>
              </div>
            </div>
            {conflictingRoles && <p className="err_info mt-2"><FontAwesomeIcon icon={faInfoCircle}/>&nbsp;{conflictingRolesMsg}</p>}
          </section>

          <div className="department-compact-columns">
            <section className="settings-form-section department-compact-section">
              <div className="settings-form-section-heading department-compact-heading">
                <div><span className="settings-form-section-kicker">Planowanie</span><h6>Reguły działu</h6></div>
              </div>
              <div className="department-form-grid department-form-grid-compact">
                <div className="dictionary-form-field">
                  <FieldLabel htmlFor="vacationLimit" tooltip="Maksymalna liczba jednocześnie zatwierdzonych urlopów. Puste pole wyłącza limit.">Limit urlopów</FieldLabel>
                  {input("vacationLimit", {type: "number", min: 1, max: 10000, step: 1, placeholder: "Brak"})}
                </div>
                <div className="dictionary-form-field">
                  <FieldLabel htmlFor="vacationLimitAction" tooltip="Określa, czy przekroczenie limitu tylko ostrzega, czy blokuje zatwierdzenie kolejnego urlopu.">Po przekroczeniu</FieldLabel>
                  <select id="vacationLimitAction" className="form-select form-select-sm" value={currentItem.vacationLimitAction || "WARN"} onChange={event => setField("vacationLimitAction", event.target.value)}>
                    <option value="WARN">Ostrzegaj</option>
                    <option value="BLOCK">Blokuj</option>
                  </select>
                </div>
                <div className="dictionary-form-field">
                  <FieldLabel htmlFor="scheduleRequiredDaysOffPerWeek" tooltip="Nadpisuje globalną liczbę wymaganych dni wolnych dla pracowników tego działu, chyba że pracownik ma własne ustawienie.">Dni wolne / tydz.</FieldLabel>
                  {input("scheduleRequiredDaysOffPerWeek", {type: "number", min: 0, max: 7, placeholder: inherited("scheduleDefaultRequiredDaysOffPerWeek", "globalne")})}
                </div>
                <div className="dictionary-form-field">
                  <FieldLabel htmlFor="scheduleMaxConsecutiveWorkDays" tooltip="Nadpisuje globalny limit kolejnych dni pracy dla działu. Ustawienie pracownika ma pierwszeństwo.">Maks. dni z rzędu</FieldLabel>
                  {input("scheduleMaxConsecutiveWorkDays", {type: "number", min: 1, max: 366, placeholder: inherited("scheduleDefaultMaxConsecutiveWorkDays", "globalne")})}
                </div>
                <div className="dictionary-form-field">
                  <FieldLabel htmlFor="scheduleConsecutiveDaysWarningThreshold" tooltip="Od tej liczby kolejnych dni pracy system pokazuje ostrzeżenie dla pracowników działu.">Próg ostrzeżenia</FieldLabel>
                  {input("scheduleConsecutiveDaysWarningThreshold", {type: "number", min: 1, max: 366, placeholder: inherited("scheduleConsecutiveDaysWarningThreshold", "globalny")})}
                </div>
                <div className="dictionary-form-field">
                  <FieldLabel htmlFor="scheduleDayOffMinimumAdvanceDays" tooltip="Minimalne wyprzedzenie dla prośby o dzień wolny. Puste pole oznacza wartość globalną.">Wolne z wyprzedz.</FieldLabel>
                  {input("scheduleDayOffMinimumAdvanceDays", {type: "number", min: 0, max: 3650, placeholder: inherited("scheduleDayOffMinimumAdvanceDays", "globalne")})}
                </div>
                <div className="dictionary-form-field">
                  <FieldLabel htmlFor="scheduleVacationMinimumAdvanceDays" tooltip="Minimalne wyprzedzenie dla wniosku urlopowego. Puste pole oznacza wartość globalną.">Urlop z wyprzedz.</FieldLabel>
                  {input("scheduleVacationMinimumAdvanceDays", {type: "number", min: 0, max: 3650, placeholder: inherited("scheduleVacationMinimumAdvanceDays", "globalne")})}
                </div>
              </div>
            </section>

            <section className="settings-form-section department-compact-section">
              <div className="settings-form-section-heading department-compact-heading">
                <div><span className="settings-form-section-kicker">Ekonomia</span><h6>Domyślne cele</h6></div>
              </div>
              <div className="department-form-grid department-form-grid-compact">
                <div className="dictionary-form-field">
                  <FieldLabel htmlFor="defaultTargetColPercent" tooltip="Domyślny %COL TARGET używany w dniach planowania, które nie mają własnej wartości.">COL TARGET %</FieldLabel>
                  {input("defaultTargetColPercent", {type: "number", min: 0, max: 100, step: "0.01"})}
                </div>
                <div className="dictionary-form-field">
                  <FieldLabel htmlFor="defaultTargetSpmh" tooltip="Domyślny SPMH TARGET używany w dniach planowania bez własnej wartości.">SPMH TARGET</FieldLabel>
                  {input("defaultTargetSpmh", {type: "number", min: "0.01", step: "0.01"})}
                </div>
                <div className="dictionary-form-field">
                  <FieldLabel htmlFor="defaultLaborHoursBudget" tooltip="Domyślny budżet roboczogodzin na dzień. Wartość ustawiona dla konkretnego dnia ma pierwszeństwo.">Budżet r-g</FieldLabel>
                  {input("defaultLaborHoursBudget", {type: "number", min: 0, step: "0.01"})}
                </div>
                <div className="dictionary-form-field">
                  <FieldLabel htmlFor="defaultMinimumStaff" tooltip="Domyślna minimalna obsada dnia. Można ją nadpisać w planowaniu konkretnego dnia.">Min. obsada</FieldLabel>
                  {input("defaultMinimumStaff", {type: "number", min: 0, step: 1})}
                </div>
                <div className="dictionary-form-field">
                  <FieldLabel htmlFor="defaultMaximumStaff" tooltip="Domyślna maksymalna obsada dnia. Można ją nadpisać w planowaniu konkretnego dnia.">Maks. obsada</FieldLabel>
                  {input("defaultMaximumStaff", {type: "number", min: 0, step: 1})}
                </div>
              </div>
            </section>
          </div>

          <section className="settings-form-section department-compact-section">
            <div className="settings-form-section-heading department-compact-heading">
              <div><span className="settings-form-section-kicker">Generator</span><h6>Priorytet kryteriów</h6></div>
            </div>
            <div className="department-criteria-list">
              {criteriaOrder.map((value, index) => {
                const criterion = CRITERIA.find(item => item.value === value);
                return (
                  <div className="department-criterion" key={value} title={criterion.tooltip}>
                    <span className="department-criterion-index">{index + 1}</span>
                    <span>{criterion.label}</span>
                    <HelpTooltip id={`criterion-${value}`} text={criterion.tooltip}/>
                    <div className="department-criterion-actions">
                      <button type="button" className="btn btn-sm btn-outline-secondary" disabled={index === 0} onClick={() => moveCriterion(index, -1)} title="Przenieś wyżej"><FontAwesomeIcon icon={faArrowUp}/></button>
                      <button type="button" className="btn btn-sm btn-outline-secondary" disabled={index === criteriaOrder.length - 1} onClick={() => moveCriterion(index, 1)} title="Przenieś niżej"><FontAwesomeIcon icon={faArrowDown}/></button>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          {!currentFormState.formAddingDataMode && (
            <section className="settings-form-section department-compact-section">
              <div className="settings-form-section-heading department-compact-heading">
                <div><span className="settings-form-section-kicker">Zapotrzebowanie</span><h6>Domyślne plany</h6></div>
              </div>
              <div className="department-default-plans">
                {PERIODS.map(period => {
                  const plans = resourcePlans.filter(plan => plan.periodType === period.type);
                  return (
                    <div className="dictionary-form-field" key={period.field}>
                      <FieldLabel htmlFor={period.field} tooltip={`Plan ${period.label.toLowerCase()} automatycznie podpowiadany przy tworzeniu grafiku tego działu.`}>{period.label}</FieldLabel>
                      <select id={period.field} className="form-select form-select-sm" value={currentItem[period.field] ?? ""} onChange={event => setField(period.field, event.target.value)}>
                        <option value="">Brak domyślnego</option>
                        {plans.map(plan => <option key={plan.id} value={plan.id}>{plan.name}</option>)}
                      </select>
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          <section className="settings-form-section department-compact-section">
            <div className="settings-form-section-heading department-compact-heading">
              <div><span className="settings-form-section-kicker">Wygląd</span><h6>Kolor</h6></div>
            </div>
            <div className="department-color-layout department-color-layout-compact">
              <div className="department-color-picker-wrap"><HexColorPicker color={currentItem?.color || itemColor} onChange={changeColor}/></div>
              <div className="department-color-info">
                <div className="department-color-sample" style={{
                  "--department-color": currentItem.color || itemColor || "#7b157e",
                  "--department-foreground": setForegroundColor(currentItem.color || itemColor || "#7b157e")
                }}>{currentItem.name || "Nazwa działu"}</div>
                <strong>{currentItem.color || itemColor || "#ffffff"}</strong>
              </div>
            </div>
          </section>

          {!currentFormState.formAddingDataMode && (
            <section className="settings-form-section department-compact-section">
              <div className="settings-form-section-heading department-compact-heading">
                <div><span className="settings-form-section-kicker">Powiązania</span><h6>Stanowiska i pracownicy</h6></div>
                <div className="department-relations-summary"><span>{positionsList.length} stanowisk</span><span>{departmentEmployeesList.length} pracowników</span></div>
              </div>
              <div className="department-relations-grid">
                <div className="department-relation-card">
                  <div className="department-relation-card-heading"><strong>Stanowiska</strong><span>{positionsList.length}</span></div>
                  <RelatedItemsList label="" propertyName="positionId" required={false} state={state} itemsList={positionsList} itemName="positions" displayNames={["name"]}/>
                </div>
                <div className="department-relation-card">
                  <div className="department-relation-card-heading"><strong>Pracownicy</strong><span>{departmentEmployeesList.length}</span></div>
                  <RelatedItemsList label="" propertyName="employeeId" required={false} state={state} itemsList={departmentEmployeesList} itemName="employees" displayNames={["firstName", "lastName"]}/>
                </div>
              </div>
            </section>
          )}

          {validationError && <p className="err_info mt-2"><FontAwesomeIcon icon={faInfoCircle}/>&nbsp;{validationError}</p>}
          {updateConflict && <p className="err_info mt-2"><FontAwesomeIcon icon={faInfoCircle}/>&nbsp;Nie można zapisać działu, ponieważ istnieje już wpis o tej nazwie lub skrócie.</p>}
        </form>
      </SettingsEntityModal>

      <DeleteWarningModal state={state} onDelete={onDelete} deleteItemName={itemName}/>
    </div>
  );
};

export default Department;

